import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from '@/components/ui/toast';

const SEND_MASS_EMAIL = gql`
  mutation emailAllUsers(
    $preview: Boolean!
    $subject: String!
    $body: String!
  ) {
    emailAllUsers(
      preview: $preview
      subject: $subject
      body: $body
    ) {
      message
      responseCode
    }
  }
`;

interface SendMassEmailData {
  sendMassEmail: {
    message: string;
    responseCode: number;
  };
}

interface SendMassEmailVars {
  preview: boolean;
  subject: string;
  body: string;
}

export const MassEmailPage: React.FC = () => {
  const editorContainerRef = React.useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<boolean>(true);
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [sendMassEmail, { loading }] = useMutation<
    SendMassEmailData,
    SendMassEmailVars
  >(SEND_MASS_EMAIL);

  React.useEffect(() => {
    const labels: Array<[string, string]> = [
      ['.ql-header .ql-picker-label', 'Heading level'],
      ['button.ql-bold', 'Bold'],
      ['button.ql-italic', 'Italic'],
      ['button.ql-underline', 'Underline'],
      ['button.ql-link', 'Insert link'],
      ['button.ql-list[value="ordered"]', 'Numbered list'],
      ['button.ql-list[value="bullet"]', 'Bulleted list'],
      ['button.ql-clean', 'Clear formatting'],
    ];
    labels.forEach(([selector, label]) => {
      editorContainerRef.current?.querySelectorAll<HTMLElement>(selector).forEach((control) => {
        control.setAttribute('aria-label', label);
      });
    });
    editorContainerRef.current?.querySelectorAll<HTMLElement>('.ql-header .ql-picker-item').forEach((control) => {
      const value = control.getAttribute('data-value');
      control.setAttribute('aria-label', value ? `Heading ${value}` : 'Normal text');
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { data } = await sendMassEmail({
        variables: { preview, subject, body },
      });

      if (data?.sendMassEmail.responseCode === 0) {
        toast.success(data.sendMassEmail.message || 'Email sent successfully.');
        setSubject('');
        setBody('');
        setPreview(true);
        return;
      }

      toast.error(data?.sendMassEmail.message || 'Failed to send email.');
    } catch (err: any) {
      toast.error(err.message || 'Error sending email.');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 text-sm">
      <h1 className="text-xl font-semibold text-gray-900 mb-4">Send Mass Email</h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <span className="block font-medium text-gray-700 mb-1">Mode</span>
          <div className="flex items-center space-x-6">
            <label className="inline-flex items-center text-gray-700">
              <input
                type="radio"
                name="mode"
                checked={preview}
                onChange={() => setPreview(true)}
                className="h-4 w-4 text-blue-600"
              />
              <span className="ml-2">Preview (current user only)</span>
            </label>
            <label className="inline-flex items-center text-gray-700">
              <input
                type="radio"
                name="mode"
                checked={!preview}
                onChange={() => setPreview(false)}
                className="h-4 w-4 text-blue-600"
              />
              <span className="ml-2">All users</span>
            </label>
          </div>
        </div>

        <div>
          <label htmlFor="subject" className="block font-medium text-gray-700 mb-1">
            Subject
          </label>
          <input
            id="subject"
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>

        <div ref={editorContainerRef}>
          <label htmlFor="body" className="block font-medium text-gray-700 mb-1">
            Body
          </label>
          <ReactQuill
            theme="snow"
            value={body}
            onChange={setBody}
            modules={{ toolbar: true }}
            formats={[
              'header',
              'bold',
              'italic',
              'underline',
              'list',
              'bullet',
              'link',
            ]}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Sending…' : 'Send Email'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MassEmailPage;
